import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { confirmEmailChange, isApiError } from 'src/services/client';
import { useAuth, UseAuthReturn } from 'src/pages/SignIn/hooks/useAuth';
import { parseSearchPreservingPlus } from 'src/utils/parseSearchParams';

type ConfirmStep = 'confirming' | 'success' | 'error';

export interface UseConfirmEmailChangeReturn {
  step: ConfirmStep;
  errorMessage: string;
  email: string;
}

export const useConfirmEmailChange = (): UseConfirmEmailChangeReturn => {
  const location = useLocation();

  const { clearAuthStorage }: UseAuthReturn = useAuth();

  const [step, setStep] = useState<ConfirmStep>('confirming');
  const [errorMessage, setErrorMessage] = useState('');

  const params: URLSearchParams = parseSearchPreservingPlus(location.search);
  const email: string = params.get('email') ?? '';

  const confirm = async () => {
    clearAuthStorage();

    const code: string = params.get('code') ?? '';

    if (!code || !email) {
      setErrorMessage(
        'This link is invalid or has expired. Please request a new one.'
      );
      setStep('error');
      return;
    }

    const response: any = await confirmEmailChange(code, email);

    if (isApiError(response) || !response.ok) {
      setErrorMessage(
        response.message ?? 'Something went wrong. The link may have expired.'
      );

      setStep('error');
      return;
    }

    setStep('success');
  };

  useEffect(() => {
    confirm();
  }, [location.search]);

  return { step, errorMessage, email };
};
